import { useMemo } from 'react';
import { SearchableSelect } from '@/Components/shared/SearchableSelect';
import {
  useAvailableSocieties,
  type AvailableSocietyAction,
} from '@/routes/outil-pro/hooks/societies/useAvailableSocieties';

interface EligibleSocietySelectorProps {
  /** Action pour laquelle les sociétés sont filtrées (contract / deliveryProof / presaleSheet). */
  action: AvailableSocietyAction;
  value: string | undefined;
  onChange: (societyId: string | undefined) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  dataTestId?: string;
  size?: 'default' | 'sm';
}

/**
 * Sélecteur de société éligible pour une action donnée (création contrat,
 * attestation, brochure avant-vente). Rendu uniquement : le parent reste
 * responsable de l'auto-sélection en cas de mono-société et du gating
 * (condition d'affichage, typiquement isExternalUser + length > 1).
 *
 * Utilise SearchableSelect (recherche côté client) : un user de tête de groupe
 * peut avoir de nombreuses agences. Pas de recherche serveur via /societies,
 * qui exige `society.list`, absent des rôles externes hors ADMIN ; la liste
 * vient de /external-users-v2/:id/societies, accessible à tout user pour
 * lui-même.
 */
export const EligibleSocietySelector = ({
  action,
  value,
  onChange,
  label = 'Société',
  placeholder = 'Rechercher une société...',
  required = false,
  error,
  disabled = false,
  dataTestId,
  size = 'default',
}: EligibleSocietySelectorProps) => {
  const { societies: eligibleSocieties } = useAvailableSocieties(action);

  const options = useMemo(
    () =>
      eligibleSocieties.map((s) => ({
        value: s.societyId,
        label: s.societyName + (s.isPrimary ? ' (principale)' : ''),
      })),
    [eligibleSocieties],
  );

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={options}
      label={label}
      placeholder={placeholder}
      required={required}
      error={error}
      disabled={disabled}
      dataTestId={dataTestId}
      size={size}
    />
  );
};
